import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AssignmentWorkspace = ({
  selectedPaper,
  selectedReviewer,
  papers,
  reviewers,
  onAssign,
  onRemove,
  calculateExpertiseMatch,
  detectConflicts
}) => {
  const getAssignedReviewers = () => {
    if (!selectedPaper) return [];
    return reviewers?.filter(r => selectedPaper?.assignedReviewers?.includes(r?.id));
  };

  const canAssign = () => {
    if (!selectedPaper || !selectedReviewer) return false;
    if (selectedPaper?.assignedReviewers?.includes(selectedReviewer?.id)) return false;
    return true;
  };

  const getConflicts = () => {
    if (!selectedPaper || !selectedReviewer) return [];
    return detectConflicts(selectedPaper, selectedReviewer);
  };

  const getExpertiseMatch = () => {
    if (!selectedPaper || !selectedReviewer) return 0;
    return calculateExpertiseMatch(selectedPaper?.keywords, selectedReviewer?.subjectAreas);
  };

  if (!selectedPaper && !selectedReviewer) {
    return (
      <div className="p-8 text-center">
        <Icon name="MousePointerClick" size={48} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">Select a paper and reviewer to begin assignment</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Selected Paper Info */}
      {selectedPaper && (
        <div className="bg-muted/40 rounded-lg p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-sm font-semibold text-foreground">Selected Paper</h3>
            <button
              onClick={() => {/* Clear selection handled by parent */}}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
          <p className="text-sm text-foreground mb-2 line-clamp-2">{selectedPaper?.title}</p>
          <p className="text-xs text-muted-foreground mb-3">{selectedPaper?.authorName}</p>
          
          {selectedPaper?.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {selectedPaper?.keywords?.map((keyword, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary"
                >
                  {keyword}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Assigned Reviewers:</span>
            <span className="font-medium text-foreground">
              {selectedPaper?.assignedReviewers?.length}/{selectedPaper?.requiredReviewers}
            </span>
          </div>
        </div>
      )}

      {/* Selected Reviewer Info */}
      {selectedReviewer && (
        <div className="bg-muted/40 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-2">Selected Reviewer</h3>
          <p className="text-sm text-foreground mb-1">
            {selectedReviewer?.firstName} {selectedReviewer?.lastName}
          </p>
          <p className="text-xs text-muted-foreground mb-3">{selectedReviewer?.institution}</p>
          
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Current Workload:</span>
              <span className="font-medium text-foreground">{selectedReviewer?.currentWorkload} papers</span>
            </div>
            {selectedPaper && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Expertise Match:</span>
                <span className={`font-medium ${
                  getExpertiseMatch() >= 60 ? 'text-success' :
                  getExpertiseMatch() >= 30 ? 'text-warning': 'text-error '
                }`}>
                  {getExpertiseMatch()}%
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conflicts Warning */}
      {selectedPaper && selectedReviewer && getConflicts()?.length > 0 && (
        <div className="bg-error-subtle border border-error-border rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="AlertTriangle" size={16} className="text-error mr-2 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-error mb-1">
                Conflict of Interest Detected
              </h4>
              <ul className="text-xs text-error space-y-1">
                {getConflicts()?.map((conflict, idx) => (
                  <li key={idx}>• {conflict?.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Action */}
      {selectedPaper && selectedReviewer && (
        <div>
          <Button
            onClick={() => onAssign(selectedPaper?.id, selectedReviewer?.id)}
            disabled={!canAssign()}
            className="w-full"
          >
            <Icon name="UserPlus" size={16} className="mr-2" />
            {selectedPaper?.assignedReviewers?.includes(selectedReviewer?.id)
              ? 'Already Assigned' :'Assign Reviewer to Paper'
            }
          </Button>
        </div>
      )}

      {/* Current Assignments */}
      {selectedPaper && getAssignedReviewers()?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Current Assignments</h3>
          <div className="space-y-2">
            {getAssignedReviewers()?.map((reviewer) => (
              <div
                key={reviewer?.id}
                className="flex items-center justify-between p-3 bg-muted rounded-lg"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    {reviewer?.firstName} {reviewer?.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground">{reviewer?.institution}</p>
                </div>
                <button
                  onClick={() => onRemove(selectedPaper?.id, reviewer?.id)}
                  className="text-error hover:text-error "
                  title="Remove assignment"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentWorkspace;
